<script lang="ts">
  import { _ } from 'svelte-i18n';
  import { Sparkles } from 'lucide-svelte';

  import { embeddingQuery } from '$lib/stores/selection';

  /**
   * What the company does, described rather than named.
   *
   * # Meaning, not wording
   *
   * The text is embedded and the candidates are ranked by how close they sit to
   * it, so it reaches companies that never use the customer's words. Asked for
   * "Beratung für Datenschutz und Informationssicherheit", Zug returns 432
   * ranked companies led by PGCR Consulting and GRC Consulting — neither of
   * which has "Datenschutz" written anywhere.
   *
   * That is why this is the one text filter rather than one of two. A literal
   * keyword filter stood beside it for an afternoon and was removed: it answers
   * a narrower question than this does, and two text boxes asking almost the
   * same thing is a choice the customer should not have to make.
   *
   * # Everything behind it already existed
   *
   * `embedding_query` on SelectionFilters, the embedder, and the ranking in
   * internal/search. Nothing in the panel wrote to it, so the feature was
   * reachable only through the assistant and looked missing.
   */

  /** Rendered inside a guided step, where there is no checkbox to switch on. */
  export let embedded = false;

  let useDescription = false;

  function toggleDescription() {
    useDescription = !useDescription;
    if (!useDescription) embeddingQuery.set('');
  }

  // Follow the store when something else changes it — a filter pill being
  // cleared, the assistant writing one, or Start Over.
  $: if (!$embeddingQuery.trim() && !embedded) {
    useDescription = false;
  } else if ($embeddingQuery.trim()) {
    useDescription = true;
  }
</script>

<div class={embedded ? '' : useDescription ? 'm-2 rounded-control bg-surface-well p-3' : 'p-3'}>
  {#if !embedded}
    <div class="mb-3">
      <label class="group flex cursor-pointer items-center">
        <input
          type="checkbox"
          checked={useDescription}
          on:click={toggleDescription}
          class="mr-2 h-3 w-3 rounded border-gray-300 text-accent-600 focus:ring-1 focus:ring-accent-200"
        />
        <Sparkles class="filter-mark mr-2" />
        <span
          class="text-xs font-semibold text-gray-900 transition-colors group-hover:text-gray-700"
        >
          {$_('filters.description.title')}
        </span>
      </label>
    </div>
  {/if}

  {#if useDescription || embedded}
    <div
      class="rounded-control border border-hairline bg-white px-3 py-2.5 transition-colors focus-within:border-accent-400"
    >
      <!--
        A textarea, because this takes a sentence. A single line would show six
        words of a twenty-word description and hide what is being matched on.
      -->
      <textarea
        rows="3"
        bind:value={$embeddingQuery}
        placeholder={$_('form.placeholders.describeService')}
        class="input-plain w-full resize-none p-0 text-sm leading-5 text-gray-900 placeholder:text-gray-400"
      ></textarea>
    </div>

    <p class="mt-1.5 text-xs text-gray-500">{$_('filters.description.hint')}</p>
  {/if}
</div>
